import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { backend, API_URL } from "../../../../config";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/styles";

const CategorySidebar = ({ onCategoryClick, selectedCategoryId }) => {
  const theme = useTheme();
  const history = useHistory();
  const [categories, setCategories] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const filterInactiveCategories = useCallback((categories) => {
    return categories
      .filter((category) => category.active)
      .map((category) => ({
        ...category,
        children: category.children
          ? filterInactiveCategories(category.children)
          : [],
      }));
  }, []);

  const findParentIds = useCallback((nodes, targetId, parents = []) => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return parents;
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        const found = findParentIds(node.children, targetId, [
          ...parents,
          node.id,
        ]);
        if (
          found.length > 0 ||
          node.children.some((child) => child.id === targetId)
        ) {
          return found;
        }
      }
    }
    return [];
  }, []);

  const getAllChildrenIds = useCallback((node) => {
    let ids = [];
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((child) => {
        ids.push(child.id);
        ids = [...ids, ...getAllChildrenIds(child)];
      });
    }
    return ids;
  }, []);

  const findAllDescendantIds = useCallback(
    (nodes, targetId) => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return getAllChildrenIds(node);
        }
        if (Array.isArray(node.children) && node.children.length > 0) {
          const found = findAllDescendantIds(node.children, targetId);
          if (found.length > 0) {
            return found;
          }
        }
      }
      return [];
    },
    [getAllChildrenIds],
  );

  const hasChildren = (node) =>
    Array.isArray(node?.children) && node.children.length > 0;

  const toggleExpand = (nodeId, e) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const isExpanded = (nodeId) => expandedIds.has(nodeId);

  const getDepthStyles = (depth) => {
    const paddingLeft = depth === 0 ? 1.5 : depth === 1 ? 2 : 3;
    const marginLeft = depth === 0 ? 0 : depth === 1 ? 1 : 2;
    const bgColor =
      depth === 0 ? "transparent" : depth === 1 ? "grey.50" : "grey.100";
    const borderLeft = depth === 0 ? undefined : `4px solid`;
    const borderLeftColor =
      depth === 0
        ? undefined
        : depth === 1
          ? "primary.light"
          : "primary.lighter";

    return {
      pl: paddingLeft,
      ml: marginLeft,
      bgcolor: bgColor,
      borderLeft,
      borderLeftColor,
      borderRadius: 2,
      boxShadow: depth > 0 ? "inset 0 0 0 1px rgba(0,0,0,0.05)" : undefined,
    };
  };

  const handleCategoryClick = (node) => {
    if (onCategoryClick) {
      onCategoryClick(node);
    } else {
      history.push(`/products?category=${node.id}`);
    }
  };

  const renderCategoryNode = (node, depth = 0) => {
    const nodeHasChildren = hasChildren(node);
    const nodeIsExpanded = isExpanded(node.id);
    const isSelected = parseInt(selectedCategoryId) === node.id;

    return (
      <Fragment key={`cat-${node.id}`}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => handleCategoryClick(node)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleCategoryClick(node);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            mb: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: isSelected ? "primary.main" : "divider",
            backgroundColor: isSelected ? "primary.light" : "transparent",
            cursor: "pointer",
            boxShadow: isSelected
              ? "0 4px 12px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(0,0,0,0.05)"
              : "inset 0 0 0 1px rgba(0,0,0,0.02)",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "primary.main",
              color: isSelected ? "primary.main" : "primary.main",
              backgroundColor: "action.hover",
              boxShadow: isSelected
                ? "0 6px 18px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(0,0,0,0.05)"
                : "0 6px 18px rgba(0,0,0,0.08)",
              transform: "translateY(-1px)",
            },
            ...getDepthStyles(depth),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isSelected ? 700 : depth === 0 ? 700 : 600,
              flex: 1,
              color: isSelected ? "primary.main" : "inherit",
            }}
          >
            {node.name}
          </Typography>
          {nodeHasChildren && (
            <Tooltip title={nodeIsExpanded ? "Collapse" : "Expand"}>
              <IconButton
                size="small"
                onClick={(e) => toggleExpand(node.id, e)}
                sx={{
                  p: 0.25,
                  mr: -0.5,
                  transition: "transform 0.2s ease",
                }}
              >
                {nodeIsExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {nodeHasChildren && nodeIsExpanded && (
          <Box sx={{ mt: 0.5 }}>
            {node.children.map((child) => renderCategoryNode(child, depth + 1))}
          </Box>
        )}
      </Fragment>
    );
  };

  useEffect(() => {
    const getCategories = async () => {
      await backend
        .get(`${API_URL}productcategories?no_page=1`)
        .then(({ data }) => {
          const dataFiltered = filterInactiveCategories(data);
          setCategories(dataFiltered);

          if (selectedCategoryId) {
            const selectedIdInt = parseInt(selectedCategoryId);
            const parentsToExpand = findParentIds(dataFiltered, selectedIdInt);
            const descendantsToExpand = findAllDescendantIds(
              dataFiltered,
              selectedIdInt,
            );

            setExpandedIds(
              new Set([
                ...parentsToExpand,
                selectedIdInt,
                ...descendantsToExpand,
              ]),
            );
          }
        });
    };

    getCategories();
  }, [
    filterInactiveCategories,
    findAllDescendantIds,
    findParentIds,
    selectedCategoryId,
  ]);

  return (
    <Box
      sx={{
        position: "sticky",
        top: theme.spacing(2),
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        pr: { sm: 1, md: 2 },
      }}
    >
      {/* <Typography variant="h6" sx={{ pl: 0.5 }}>
        Categories
      </Typography> */}
      <Box>{categories.map((root) => renderCategoryNode(root, 0))}</Box>
    </Box>
  );
};

export default CategorySidebar;
